import { PlaneLanding, PlaneTakeoff, AlertTriangle, Flame, Map } from 'lucide-react'
import {
  getMockServicesForMovement,
  getMockStackingFlag,
  getMockDurationMinutes,
} from './mock-service-data'
import type { ScheduleItem } from './useTodayBoardData'
import type { DetailLevel } from './types'

interface GanttRowProps {
  item: ScheduleItem
  detailLevel: DetailLevel
  /** Start hour of the visible time axis */
  startHour: number
  /** Total hours displayed */
  totalHours: number
}

export function GanttRow({ item, detailLevel, startHour, totalHours }: GanttRowProps) {
  const { data } = item
  const isArrival = item.movementType === 'arriving'
  const hasStacking = getMockStackingFlag(item)
  const durationMin = getMockDurationMinutes(item)
  const services = getMockServicesForMovement(item)

  // Client name
  const clientName =
    data.leaseContract?.clientName ??
    data.transientStay?.clientName ??
    data.aircraft.owner?.companyName ??
    data.aircraft.ownerName ??
    null

  // Space
  const space = data.unit?.unitCode ?? data.area?.name ?? null

  // Time display
  let timeLabel: string | null = null
  let barLeftPercent = 0
  if (item.scheduledTime) {
    const d = new Date(item.scheduledTime)
    timeLabel = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    const minutesSinceStart = (d.getHours() - startHour) * 60 + d.getMinutes()
    barLeftPercent = Math.max(0, (minutesSinceStart / (totalHours * 60)) * 100)
  } else if (item.sortTime) {
    const d = new Date(item.sortTime)
    timeLabel = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    const minutesSinceStart = (d.getHours() - startHour) * 60 + d.getMinutes()
    barLeftPercent = Math.max(0, (minutesSinceStart / (totalHours * 60)) * 100)
  }

  // Bar width from duration
  const barWidthPercent = Math.max(2, (durationMin / (totalHours * 60)) * 100)

  return (
    <div className="flex items-stretch border-b hover:bg-accent/30 group transition-colors">
      {/* Identity column */}
      <div className="w-[300px] shrink-0 p-2.5 pr-3 border-r flex flex-col justify-center bg-card group-hover:bg-accent/20 relative z-20">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-0.5 rounded ${isArrival ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-violet-600'}`}>
              {isArrival ? <PlaneLanding className="h-3.5 w-3.5" /> : <PlaneTakeoff className="h-3.5 w-3.5" />}
            </div>
            <div>
              <div className="font-bold text-sm tracking-tight flex items-baseline gap-1.5">
                {data.aircraft.tailNumber}
                <span className="text-[10px] text-muted-foreground font-semibold">{data.aircraft.typeCode}</span>
              </div>
              {clientName && (
                <div className="text-[11px] font-medium text-muted-foreground truncate max-w-[140px] leading-tight mt-0.5">
                  {clientName}
                </div>
              )}
            </div>
          </div>
          <div className="text-right flex flex-col items-end">
            {timeLabel && <span className="font-mono text-xs font-bold">{timeLabel}</span>}
            {space && (
              <span className="text-[10px] font-bold bg-muted px-1 py-0.5 rounded mt-0.5 truncate max-w-[80px]">
                {space}
              </span>
            )}
          </div>
        </div>
        {hasStacking && (
          <div className="mt-1 ml-6 text-[10px] font-medium text-amber-700 flex items-center gap-1 bg-amber-50 px-1.5 py-0.5 rounded w-max border border-amber-200">
            <AlertTriangle className="h-2.5 w-2.5" /> Requires Hangar Pull
          </div>
        )}
      </div>

      {/* Gantt bar area */}
      <div className="flex-1 relative min-h-[52px] z-10 flex items-center my-0.5 group/row">
        {/* The bar */}
        <div
          className="absolute h-6 z-10"
          style={{ left: `${barLeftPercent}%`, width: `${barWidthPercent}%`, minWidth: '40px' }}
        >
          {detailLevel === 'L1' || services.length === 0 ? (
            // L1: single solid bar
            <div className={`h-full w-full rounded-md border shadow-xs transition-all cursor-pointer hover:ring-2 ${
              hasStacking
                ? 'bg-amber-200 border-amber-300 hover:ring-amber-400'
                : isArrival
                  ? 'bg-blue-200 border-blue-300 hover:ring-blue-400'
                  : 'bg-violet-200 border-violet-300 hover:ring-violet-400'
            }`} />
          ) : (
            // L2+: segmented bar
            <SegmentedBar isArrival={isArrival} hasStacking={hasStacking} />
          )}

          {/* Hover tooltip */}
          <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-popover text-popover-foreground text-[10px] font-bold px-2 py-1 rounded shadow-lg opacity-0 group-hover/row:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 border">
            {timeLabel ?? '—'} · {durationMin}min · {services.length} tasks
          </div>
        </div>

        {/* Start point marker */}
        <div
          className="absolute w-2 h-2 rounded-full bg-foreground border-2 border-background shadow-sm z-20 top-1/2 -translate-y-1/2"
          style={{ left: `calc(${barLeftPercent}% - 4px)` }}
        />
      </div>
    </div>
  )
}

function SegmentedBar({ isArrival, hasStacking }: { isArrival: boolean; hasStacking: boolean }) {
  return (
    <div className="flex h-full w-full rounded-md border shadow-xs overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all bg-card">
      <div className="h-full w-[35%] bg-blue-100 border-r border-blue-200/60 flex items-center justify-center text-blue-600">
        <Map className="h-3 w-3 opacity-70" />
      </div>
      <div className="h-full w-[35%] bg-emerald-100 border-r border-emerald-200/60 flex items-center justify-center text-emerald-600">
        <Flame className="h-3 w-3 opacity-70" />
      </div>
      {hasStacking ? (
        <div className="h-full w-[30%] bg-amber-300 flex items-center justify-center text-amber-900">
          <AlertTriangle className="h-3 w-3 opacity-80" />
        </div>
      ) : (
        <div className={`h-full w-[30%] ${isArrival ? 'bg-blue-50' : 'bg-violet-50'}`} />
      )}
    </div>
  )
}
